import type { FolderUser, FolderUserUpdate } from '../../types';
import type { FolderPermissionsPut } from '../../api';
import { useState } from 'react';
import { DrawerHeader, DrawerBody, DrawerFooter, Button, H3, Stack, Link } from '@workramp/ui';
import { AppState } from '~/common/explicit-exports';
import { Portal } from '~/features/common';
import WRModalsConfirm from '~/components/wr-modals/wr-modals-confirm';
import { FolderPermission } from '../FolderPermission';
import { PermissionSearch } from '../PermissionSearch';

export interface FolderPermissionsDrawerProps {
  onCancel: () => void;
  onClose: () => void;
  onUpdate: (data: FolderUserUpdate) => void;
  onAdd: (data: FolderUser) => void;
  onSave: (payload: FolderPermissionsPut) => void;
  folderName: string;
  folderUsers: FolderUser[];
  confirmCancel: boolean;
  readOnly: boolean;
}

export function FolderPermissionsDrawer({
  onClose,
  onCancel,
  folderName,
  folderUsers,
  onUpdate,
  onAdd,
  onSave,
  confirmCancel,
  readOnly,
}: FolderPermissionsDrawerProps) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleCancel = () => {
    if (confirmCancel) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    const payload = folderUsers.reduce(
      (payloadBody, { inheritedFolder, userId, groupId, accessLevel }) => {
        if (inheritedFolder) {
          // do not submit folder users inherited from a parent
          return payloadBody;
        }
        if (userId) {
          payloadBody.users.push({ userId, accessLevel });
        } else if (groupId) {
          payloadBody.groups.push({ groupId, accessLevel });
        }

        return payloadBody;
      },
      { users: [], groups: [] },
    );
    onSave(payload);
    onClose();
  };

  const handleAdd = () => {
    handleClose();
  };

  return (
    <>
      <DrawerHeader
        title={`Permissions For '${folderName}' Folder`}
        description={
          readOnly ? (
            <>
              Only Admins are able to{' '}
              <Link
                href="https://help.workramp.com/en/articles/2305711-adding-and-managing-editors"
                rel="noreferrer"
                target="_blank"
              >
                adjust permissions.
              </Link>{' '}
              <br /> Please reach out to your admin for additional access.
            </>
          ) : (
            <>
              Search for member(s) to add and assign them a{' '}
              {!AppState.isWhiteLabel ? (
                <Link
                  href="https://help.workramp.com/en/articles/2305711-adding-and-managing-editors"
                  rel="noreferrer"
                  target="_blank"
                >
                  permission type
                </Link>
              ) : (
                'permission type'
              )}
              .
            </>
          )
        }
      />

      <DrawerBody>
        <Stack size={6}>
          {!readOnly && <PermissionSearch onSelect={onAdd} />}
          <H3 size={1} weight="semibold">
            People with access
          </H3>
          <Stack size={7}>
            {folderUsers.map((user) => (
              <FolderPermission
                key={`${user.userId}-${user.groupId}-${user.inheritedFolder?.id}`}
                folderUser={user}
                onUpdate={onUpdate}
                readOnly={readOnly}
              />
            ))}
          </Stack>
        </Stack>
      </DrawerBody>

      <DrawerFooter
        actions={
          <>
            <Button variant="text" color="secondary" onPress={handleCancel} isDisabled={readOnly}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleAdd} isDisabled={readOnly}>
              Save
            </Button>
          </>
        }
      />

      <Portal>
        <WRModalsConfirm
          newUi
          isDangerConfirm
          show={showConfirmModal}
          onSave={onCancel}
          onClose={() => setShowConfirmModal(false)}
          text="Unsaved changes will be discarded. Are you sure you want to close?"
          saveButtonTitle="Close"
        />
      </Portal>
    </>
  );
}
